import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { devLog } from '../utils/devTools';
import {
  Box,
  Typography,
  Paper,
  Button, 
  Container, 
  CircularProgress, 
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { formatDate } from '../utils/dateTime';

interface RecentJob {
  jobNumber: string;
  jobDate: string;
  deponentName: string;
  caseName: string;
  jobState: string;
  firmName: string;
  lastSubmitted?: string; // ISO timestamp when job was last submitted
}

interface JobsListResponse { success: boolean; jobs: RecentJob[]; message?: string; }
interface GenerateTokenResponse { success: boolean; token?: string; message?: string; }

const JobsPage: React.FC = () => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [recentJobs, setRecentJobs] = useState<RecentJob[]>([]);
  const [filteredJobs, setFilteredJobs] = useState<RecentJob[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Search states
  const [searchType, setSearchType] = useState<'jobNumber' | 'dateRange'>('jobNumber');
  const [jobNumberSearch, setJobNumberSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // State to track if we're showing search results (to determine whether to show Status column)
  const [isSearchView, setIsSearchView] = useState(false);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Fetch recent jobs when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchRecentJobs();
    }
  }, [isAuthenticated]);

  // Update filtered jobs only when recentJobs changes
  // (we'll use the search endpoint for explicit searches)
  useEffect(() => {
    if (recentJobs.length > 0) {
      setFilteredJobs(recentJobs);
    }
  }, [recentJobs]);

  const fetchRecentJobs = async () => {
    devLog("[DEBUG] fetchRecentJobs called");
    setLoadingJobs(true);
    // Set isSearchView to false when fetching recent jobs
    setIsSearchView(false);
    try {
      devLog("[DEBUG] Making request to /api/jobs/recent");
      const response = await api.get<JobsListResponse>('/api/jobs/recent');
      devLog("[DEBUG] Recent jobs response:", response);
      if (response.success) {
        setRecentJobs(response.jobs);
        setFilteredJobs(response.jobs);
      } else {
        console.warn("Failed to load recent jobs:", response.message);
        setErrorMessage('Failed to load recent jobs');
      }
    } catch (error) {
      console.error("[DEBUG] Error fetching recent jobs:", error);
      setErrorMessage('An error occurred while fetching jobs');
    } finally {
      setLoadingJobs(false);
    }
  };

  const searchJobs = async () => {
    if (searchType === 'dateRange') {
      if (!validateDateRange()) {
        return;
      }
    }

    setLoadingJobs(true);
    try {
      const params: Record<string, string> = {
        searchType,
        // For searches, always include submitted jobs so users can see what's been done
        includeSubmitted: 'true'
      };
      
      if (searchType === 'jobNumber') {
        if (!jobNumberSearch.trim()) {
          // If no job number is provided, fetch recent jobs instead
          return fetchRecentJobs();
        }
        params.jobNumber = jobNumberSearch.trim();
      } else if (searchType === 'dateRange') {
        if (!startDate && !endDate) {
          // If no dates are provided, fetch recent jobs instead
          return fetchRecentJobs();
        }
        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;
      }

      devLog("[DEBUG] Making search request with params:", params);
      const response = await api.get<JobsListResponse>('/api/jobs/search', params);
      
      if (response.success) {
        // Set isSearchView to true when showing search results
        setIsSearchView(true);
        setFilteredJobs(response.jobs);
        if (response.jobs.length === 0) {
          setSuccessMessage("No jobs found matching your criteria");
        }
      } else {
        setErrorMessage(response.message || 'Failed to search jobs');
      }
    } catch (error) {
      console.error("[DEBUG] Error searching jobs:", error);
      setErrorMessage('An error occurred while searching for jobs');
    } finally {
      setLoadingJobs(false);
    }
  };

  const handleSearch = () => {
    searchJobs();
  };

  const clearSearch = () => {
    setJobNumberSearch('');
    setStartDate('');
    setEndDate('');
    fetchRecentJobs();
  };

  // Validate date range
  const validateDateRange = () => {
    if (!startDate || !endDate) return true;

    const start = new Date(startDate);
    const end = new Date(endDate);
    
    // Check if end date is before start date
    if (end < start) {
      setErrorMessage('End date must be after start date');
      return false;
    }

    // Check if date range is more than a month
    const oneMonth = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds
    if (end.getTime() - start.getTime() > oneMonth) {
      setErrorMessage('Date range cannot exceed one month');
      return false;
    }

    return true;
  };

  const handleJobSelect = async (jobNumber: string) => {
    try {
      setLoadingJobs(true);
      // Generate a token for this job
      const response = await api.get<GenerateTokenResponse>(`/api/jobs/generate-token/${jobNumber}`);
      
      if (response.success && response.token) {
        // Preserve the token-based route but add a query parameter to indicate authenticatedFlow
        navigate(`/worksheet/${response.token}?fromHome=true`);
      } else {
        setErrorMessage(response.message || 'Failed to generate access token');
      }
    } catch (error) {
      setErrorMessage('An error occurred while preparing the worksheet');
    } finally {
      setLoadingJobs(false);
    }
  };

  const handleCloseMessage = () => {
    setErrorMessage(null);
    setSuccessMessage(null);
  };

  if (authLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
        <Typography variant="h6" ml={2}>Checking authentication status...</Typography>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return null; // Will redirect to login page via useEffect
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Header />
      <Container component="main" sx={{ flexGrow: 1, py: 4 }}>
        <Typography variant="h4" gutterBottom>
          Your Depositions
        </Typography>
        
        {/* Search Controls */}
        <Paper sx={{ p: 3, mb: 3, backgroundColor: '#f9f9f9', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
          <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 2 }}>
            Search Depositions
          </Typography>
          <Grid container spacing={3} alignItems="flex-end">
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth variant="outlined">
                <InputLabel id="search-type-label">Search By</InputLabel>
                <Select
                  labelId="search-type-label"
                  value={searchType}
                  label="Search By"
                  onChange={(e) => setSearchType(e.target.value as 'jobNumber' | 'dateRange')}
                >
                  <MenuItem value="jobNumber">Job Number</MenuItem>
                  <MenuItem value="dateRange">Date Range</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {searchType === 'jobNumber' ? (
              <Grid item xs={12} sm={7}>
                <TextField
                  fullWidth
                  label="Job Number"
                  value={jobNumberSearch}
                  onChange={(e) => setJobNumberSearch(e.target.value)}
                  variant="outlined"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                />
              </Grid>
            ) : (
              <>
                <Grid item xs={12} sm={3.5}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSearch();
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={3.5}>
                  <TextField
                    fullWidth
                    label="End Date"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSearch();
                      }
                    }}
                  />
                </Grid>
              </>
            )}
            
            <Grid item xs={12} sm={2} container justifyContent="flex-end" spacing={1}>
              <Grid item>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SearchIcon />}
                  onClick={() => {
                    if (searchType === 'dateRange' && !validateDateRange()) {
                      return;
                    }
                    handleSearch();
                  }}
                  sx={{ minWidth: '100px' }}
                >
                  Search
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<ClearIcon />}
                  onClick={clearSearch}
                  sx={{ minWidth: '100px' }}
                >
                  Clear
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Paper>
        
        {/* Jobs Table */}
        {loadingJobs ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : filteredJobs.length === 0 ? (
          <Alert severity="info">
            {recentJobs.length === 0 
              ? "No recent depositions found." 
              : "No depositions match your search criteria."}
          </Alert>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1">
                Showing {filteredJobs.length} {filteredJobs.length === 1 ? 'deposition' : 'depositions'}
              </Typography>
            </Box>
        
          <TableContainer component={Paper} sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <Table>
              <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Job Number</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Deponent</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Case</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Firm</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>State</TableCell>
                  {/* Only show Status column when in search view */}
                  {isSearchView && (
                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  )}
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredJobs.map((job, index) => (
                  <TableRow 
                    key={job.jobNumber} 
                    hover
                    sx={{ 
                      backgroundColor: job.lastSubmitted 
                        ? '#f8f8f8' // Light gray background for submitted jobs
                        : index % 2 === 0 
                          ? 'white' 
                          : '#fafafa',
                      '&:hover': { backgroundColor: '#f0f7ff' }
                    }}
                  >
                    <TableCell>{job.jobNumber}</TableCell>
                    <TableCell>{new Date(job.jobDate + "T12:00:00").toLocaleDateString()}</TableCell>
                    <TableCell>{job.deponentName || 'N/A'}</TableCell>
                    <TableCell>{job.caseName}</TableCell>
                    <TableCell>{job.firmName || 'N/A'}</TableCell>
                    <TableCell>{job.jobState}</TableCell>
                    {/* Only show Status cell when in search view */}
                    {isSearchView && (
                      <TableCell>
                        {job.lastSubmitted ? (
                          <Typography variant="body2" color="text.secondary">
                            Submitted {formatDate(job.lastSubmitted)}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="primary">
                            Not yet submitted
                          </Typography>
                        )}
                      </TableCell>
                    )}
                    <TableCell align="center">
                      <Button 
                        variant="contained" 
                        size="small"
                        color={job.lastSubmitted ? "secondary" : "primary"}
                        onClick={() => handleJobSelect(job.jobNumber)}
                        sx={{ minWidth: '160px' }}
                      >
                        {job.lastSubmitted ? "View Worksheet" : "Complete Worksheet"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          </>
        )}
      </Container>
      <Footer />

      <Snackbar 
        open={!!errorMessage} 
        autoHideDuration={6000} 
        onClose={handleCloseMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseMessage} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>

      <Snackbar 
        open={!!successMessage} 
        autoHideDuration={6000} 
        onClose={handleCloseMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseMessage} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default JobsPage;